import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button, 
  OutlineButton, 
  Badge, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableHeader,
  FlexContainer,
  Grid
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import { EmployeeLoan } from '../../types';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const Summary = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SummaryCard = styled.div<{ type?: string }>`
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  padding: 1.5rem;
  text-align: center;
  
  h3 {
    margin: 0 0 0.5rem 0;
    color: var(--secondary-color);
    font-size: 1rem;
  }
  
  .amount {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${props => props.type === 'principal' ? 'var(--primary-color)' : 
      props.type === 'balance' ? 'var(--danger-color)' : 
      props.type === 'paid' ? 'var(--success-color)' : 'var(--text-color)'};
  }
  
  .label {
    font-size: 0.85rem;
    color: var(--dark-gray);
    margin-top: 0.5rem;
  }
`;

const DetailSection = styled.div`
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border-color);
  
  &:last-child {
    border-bottom: none;
  }
  
  .label {
    font-weight: 500;
    color: var(--dark-gray);
  }
  
  .value {
    font-weight: 600;
    color: var(--text-color);
  }
`;

const AmountText = styled.span<{ type?: 'positive' | 'negative' | 'neutral' }>`
  font-weight: 500;
  color: ${props => props.type === 'positive' ? 'var(--success-color)' : 
    props.type === 'negative' ? 'var(--danger-color)' : 'var(--text-color)'};
`;

const DateText = styled.span`
  color: var(--dark-gray);
  font-size: 0.85rem;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--dark-gray);
`;

const LoanDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [loan, setLoan] = useState<EmployeeLoan | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadLoan = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const response = await apiService.getEmployeeLoan(id);
        if (response.success && response.data) {
          setLoan(response.data);
        } else {
          setLoan(null);
        }
      } catch (error) {
        console.error('Error loading loan:', error);
        setLoan(null);
      } finally {
        setLoading(false);
      }
    };

    loadLoan();
  }, [id]);

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'TZS',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusVariant = (status: string): 'success' | 'danger' | 'warning' | 'info' | 'primary' | 'secondary' => {
    switch (status) {
      case 'Pending': return 'warning';
      case 'Approved': return 'info';
      case 'Active': return 'primary';
      case 'Completed': return 'success';
      case 'Defaulted': return 'danger';
      case 'Cancelled': return 'secondary';
      default: return 'info';
    }
  };

  if (loading) {
    return <div>Loading loan details...</div>;
  }

  if (!loan) {
    return (
      <div>
        <PageHeader>
          <h1 className="page-title">Loan Not Found</h1>
          <OutlineButton as={Link} to="/office/loans">
            Back to Loans
          </OutlineButton>
        </PageHeader>
        <Card>
          <CardBody>
            <NoDataMessage>
              <p>The requested loan could not be found.</p>
            </NoDataMessage>
          </CardBody>
        </Card>
      </div>
    );
  }

  const totalPaid = loan.paidAmount;
  const remainingBalance = loan.balance;
  const totalInterest = loan.totalAmount - loan.principal;
  const totalOwed = loan.totalAmount;

  return (
    <div>
      <PageHeader>
        <div>
          <h1 className="page-title">Loan Details</h1>
          <p style={{ color: 'var(--dark-gray)', margin: '0.5rem 0 0 0' }}>
            {loan.id} - {loan.purpose}
          </p>
        </div>
        <FlexContainer gap="0.5rem">
          <OutlineButton as={Link} to="/office/loans">
            Back to Loans
          </OutlineButton>
          {user?.role === 'MD' || user?.role === 'TD' || user?.role === 'Accountant' ? (
            <>
              {loan.status === 'Active' && (
                <Button as={Link} to={`/office/loans/${loan.id}/payment`}>
                  Make Payment
                </Button>
              )}
              <OutlineButton as={Link} to={`/office/loans/edit/${loan.id}`}>
                Edit Loan
              </OutlineButton>
            </>
          ) : null}
        </FlexContainer>
      </PageHeader>

      <Summary>
        <SummaryCard type="principal">
          <h3>Principal Amount</h3>
          <div className="amount">{formatAmount(loan.principal)}</div>
          <div className="label">Original loan amount</div>
        </SummaryCard>
        
        <SummaryCard type="balance">
          <h3>Remaining Balance</h3>
          <div className="amount">{formatAmount(remainingBalance)}</div>
          <div className="label">Amount still owed</div>
        </SummaryCard>
        
        <SummaryCard type="paid">
          <h3>Total Paid</h3>
          <div className="amount">{formatAmount(totalPaid)}</div>
          <div className="label">Principal + Interest</div>
        </SummaryCard>
        
        <SummaryCard>
          <h3>Interest Rate</h3>
          <div className="amount">{loan.interestRate}%</div>
          <div className="label">Annual interest rate</div>
        </SummaryCard>
      </Summary>

      <Grid columns="1fr 1fr" gap="1.5rem">
        <DetailSection>
          <h3 style={{ marginBottom: '1rem', color: 'var(--secondary-color)' }}>
            Loan Information
          </h3>
          <DetailRow>
            <span className="label">Loan ID:</span>
            <span className="value">{loan.id}</span>
          </DetailRow>
          <DetailRow>
            <span className="label">Purpose:</span>
            <span className="value">{loan.purpose}</span>
          </DetailRow>
          <DetailRow>
            <span className="label">Employee:</span>
            <span className="value">{loan.employeeName || 'N/A'}</span>
          </DetailRow>
          <DetailRow>
            <span className="label">Employee Role:</span>
            <span className="value">{loan.employeeRole || 'N/A'}</span>
          </DetailRow>
          <DetailRow>
            <span className="label">Principal:</span>
            <AmountText type="neutral">{formatAmount(loan.principal)}</AmountText>
          </DetailRow>
          <DetailRow>
            <span className="label">Interest Rate:</span>
            <span className="value">{loan.interestRate}%</span>
          </DetailRow>
          <DetailRow>
            <span className="label">Duration:</span>
            <span className="value">{loan.duration} months</span>
          </DetailRow>
          <DetailRow>
            <span className="label">Monthly Installment:</span>
            <AmountText type="neutral">{formatAmount(loan.installmentAmount)}</AmountText>
          </DetailRow>
          <DetailRow>
            <span className="label">Start Date:</span>
            <DateText>{formatDate(loan.startDate)}</DateText>
          </DetailRow>
          <DetailRow>
            <span className="label">End Date:</span>
            <DateText>{formatDate(loan.endDate)}</DateText>
          </DetailRow>
          <DetailRow>
            <span className="label">Status:</span>
            <Badge variant={getStatusVariant(loan.status)}>
              {loan.status}
            </Badge>
          </DetailRow>
        </DetailSection>

        <DetailSection>
          <h3 style={{ marginBottom: '1rem', color: 'var(--secondary-color)' }}>
            Financial Summary
          </h3>
          <DetailRow>
            <span className="label">Principal Amount:</span>
            <AmountText type="neutral">{formatAmount(loan.principal)}</AmountText>
          </DetailRow>
          <DetailRow>
            <span className="label">Total Interest:</span>
            <AmountText type="negative">{formatAmount(totalInterest)}</AmountText>
          </DetailRow>
          <DetailRow>
            <span className="label">Total Owed:</span>
            <AmountText type="negative">{formatAmount(totalOwed)}</AmountText>
          </DetailRow>
          <DetailRow>
            <span className="label">Amount Paid:</span>
            <AmountText type="positive">{formatAmount(totalPaid)}</AmountText>
          </DetailRow>
          <DetailRow>
            <span className="label">Remaining Balance:</span>
            <AmountText type={remainingBalance > 0 ? 'negative' : 'positive'}>
              {formatAmount(remainingBalance)}
            </AmountText>
          </DetailRow>
          <DetailRow>
            <span className="label">Progress:</span>
            <span className="value">
              {totalOwed > 0 ? ((totalPaid / totalOwed) * 100).toFixed(1) : 0}%
            </span>
          </DetailRow>
          <DetailRow>
            <span className="label">Created By:</span>
            <span className="value">{loan.createdBy}</span>
          </DetailRow>
          <DetailRow>
            <span className="label">Created Date:</span>
            <DateText>{formatDate(loan.createdAt)}</DateText>
          </DetailRow>
        </DetailSection>
      </Grid>

      <Card style={{ marginTop: '1.5rem' }}>
        <CardHeader>
          <h3>Payment History</h3>
        </CardHeader>
        <CardBody>
          {loan.installments.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Date</TableHeader>
                    <TableHeader>Amount</TableHeader>
                    <TableHeader>Principal</TableHeader>
                    <TableHeader>Interest</TableHeader>
                    <TableHeader>Remaining Balance</TableHeader>
                    <TableHeader>Status</TableHeader>
                  </TableRow>
                </TableHead>
                <tbody>
                  {loan.installments.map((installment) => (
                    <TableRow key={installment.id}>
                      <TableCell>
                        <DateText>{formatDate(installment.dueDate)}</DateText>
                      </TableCell>
                      <TableCell>
                        <AmountText type="neutral">
                          {formatAmount(installment.amount)}
                        </AmountText>
                      </TableCell>
                                             <TableCell>
                         <AmountText type="neutral">
                           {formatAmount(installment.amount)}
                         </AmountText>
                       </TableCell>
                       <TableCell>
                         <AmountText type="neutral">
                           {formatAmount(installment.amount * 0.8)}
                         </AmountText>
                       </TableCell>
                       <TableCell>
                         <AmountText type="negative">
                           {formatAmount(installment.amount * 0.2)}
                         </AmountText>
                       </TableCell>
                       <TableCell>
                         <AmountText type="neutral">
                           {formatAmount(installment.amount)}
                         </AmountText>
                       </TableCell>
                       <TableCell>
                         <Badge 
                           variant={
                             installment.status === 'Paid' ? 'success' : 
                             installment.status === 'Overdue' ? 'danger' : 'warning'
                           }
                         >
                           {installment.status}
                         </Badge>
                       </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          ) : (
            <NoDataMessage>
              <p>No payment history available for this loan.</p>
            </NoDataMessage>
          )}
        </CardBody>
      </Card>

      {loan.attachments.length > 0 && (
        <Card style={{ marginTop: '1.5rem' }}>
          <CardHeader>
            <h3>Attachments</h3>
          </CardHeader>
          <CardBody>
            <FlexContainer gap="1rem" style={{ flexWrap: 'wrap' }}>
              {loan.attachments.map((attachment) => (
                <div key={attachment.id} style={{ 
                  padding: '1rem', 
                  border: '1px solid var(--border-color)', 
                  borderRadius: 'var(--border-radius)',
                  background: 'var(--light-gray)'
                }}>
                  <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>
                    {attachment.name}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--dark-gray)' }}>
                    {attachment.type} • {attachment.size}
                  </div>
                  <OutlineButton 
                    style={{ marginTop: '0.5rem' }}
                    onClick={() => window.open(attachment.url, '_blank')}
                  >
                    Download
                  </OutlineButton>
                </div>
              ))}
            </FlexContainer>
          </CardBody>
        </Card>
      )}

      <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
        Developed by Sandfish
      </div>
    </div>
  );
};

export default LoanDetail; 